import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  AIProvider, 
  AIChatbotSettings, 
  AIServicesConfig,
  OpenAIConfig,
  AnthropicConfig,
  GoogleConfig,
  AzureConfig,
  MistralConfig,
  DeepSeekConfig,
  CustomConfig,
  ProviderConfig
} from '@/types/ai';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, Key, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AIProviderSettingsProps {
  settings: AIChatbotSettings;
  onSettingsChange: (settings: AIChatbotSettings) => void;
}

// Default provider configs for easy selection
const DEFAULT_PROVIDERS: Record<AIProvider, ProviderConfig> = {
  openai: {
    provider: 'openai',
    model: 'gpt-4o',
    temperature: 0.7,
    max_tokens: 2048
  },
  anthropic: {
    provider: 'anthropic',
    model: 'claude-3-opus-20240229',
    temperature: 0.7,
    max_tokens: 4096
  },
  google: {
    provider: 'google',
    model: 'gemini-pro',
    temperature: 0.7,
    max_tokens: 2048
  },
  azure: {
    provider: 'azure',
    model: 'gpt-4',
    resourceName: '',
    deploymentId: '',
    temperature: 0.7,
    max_tokens: 2048
  },
  mistral: {
    provider: 'mistral',
    model: 'mistral-large-latest',
    temperature: 0.7,
    max_tokens: 2048
  },
  deepseek: {
    provider: 'deepseek',
    model: 'deepseek-chat',
    temperature: 0.7,
    max_tokens: 4096
  },
  custom: {
    provider: 'custom',
    model: 'custom-model',
    endpoint: '',
    requestFormat: 'openai',
    responseFormat: 'openai',
    temperature: 0.7,
    max_tokens: 2048
  }
};

// Available models by provider
const AVAILABLE_MODELS: Record<AIProvider, string[]> = {
  openai: ['gpt-4o', 'gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  anthropic: ['claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'],
  google: ['gemini-pro', 'gemini-ultra'],
  azure: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
  mistral: ['mistral-large-latest', 'mistral-medium-latest', 'mistral-small-latest'],
  deepseek: ['deepseek-chat', 'deepseek-coder', 'deepseek-lite'],
  custom: ['custom-model']
};

// Provider display names
const PROVIDER_DISPLAY_NAMES: Record<AIProvider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google AI',
  azure: 'Azure OpenAI',
  mistral: 'Mistral AI',
  deepseek: 'DeepSeek AI',
  custom: 'Custom Provider'
};

// Request format options for custom providers
type RequestFormat = 'openai' | 'anthropic' | 'custom';
const REQUEST_FORMATS: Record<RequestFormat, string> = {
  openai: 'OpenAI Compatible',
  anthropic: 'Anthropic Compatible',
  custom: 'Custom Format'
};

export function AIProviderSettings({
  settings,
  onSettingsChange
}: AIProviderSettingsProps) {
  const [activeTab, setActiveTab] = useState<'primary' | 'validation' | 'fallback' | 'keys'>('primary');
  const [providerTestStatus, setProviderTestStatus] = useState<Record<string, { status: 'success' | 'error' | 'idle'; message?: string }>>({
    primary: { status: 'idle' },
    validation: { status: 'idle' },
    fallback: { status: 'idle' }
  });
  const hasInitializedRef = useRef(false);

  // Update AI services config
  const updateAISettings = useCallback((newConfig: Partial<AIServicesConfig>) => {
    const updatedSettings = {
      ...settings,
      aiServices: {
        ...settings.aiServices,
        ...newConfig
      }
    };
    onSettingsChange(updatedSettings);
  }, [settings, onSettingsChange]);

  useEffect(() => {
    // Initialize default values if not set (only once on mount)
    // Use ref to prevent re-initialization on every settings change
    if (!hasInitializedRef.current) {
      if (!settings.aiServices || !settings.aiServices.primary) {
        hasInitializedRef.current = true;
        updateAISettings({
          ...settings.aiServices || {},
          primary: DEFAULT_PROVIDERS.openai
        });
      } else {
        // Mark as initialized even if primary already exists
        hasInitializedRef.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - intentionally empty deps to prevent loops

  // Update a provider config
  const updateProviderConfig = (configType: 'primary' | 'validation' | 'fallback') => 
    (updates: Partial<ProviderConfig>) => {
      if (!settings.aiServices[configType]) {
        // If this config doesn't exist yet, create it with the default for the selected provider
        const providerType = updates.provider || 'openai';
        updateAISettings({
          [configType]: {
            ...DEFAULT_PROVIDERS[providerType as AIProvider],
            ...updates
          }
        });
      } else {
        // Otherwise update the existing config
        updateAISettings({
          [configType]: {
            ...settings.aiServices[configType],
            ...updates
          }
        });
      }
    };

  // Update a specific provider's API key
  const updateProviderKey = (provider: AIProvider, key: string, value: string) => {
    const currentKeys = settings.aiServices.providerKeys || {};
    
    let updatedKeys;
    if (provider === 'azure' && key === 'apiKey') {
      updatedKeys = {
        ...currentKeys,
        azure: {
          ...(currentKeys.azure || {}),
          apiKey: value
        }
      };
    } else if (provider === 'azure' && key === 'resourceName') {
      updatedKeys = {
        ...currentKeys,
        azure: {
          ...(currentKeys.azure || {}),
          resourceName: value
        }
      };
    } else if (provider === 'custom' && key === 'apiKey') {
      updatedKeys = {
        ...currentKeys,
        custom: {
          ...(currentKeys.custom || {}),
          apiKey: value
        }
      };
    } else if (provider === 'custom' && key === 'endpoint') {
      updatedKeys = {
        ...currentKeys,
        custom: {
          ...(currentKeys.custom || {}),
          endpoint: value
        }
      };
    } else {
      // Standard provider with a simple string key
      updatedKeys = {
        ...currentKeys,
        [provider]: value
      };
    }
    
    updateAISettings({ providerKeys: updatedKeys });
  };

  // Toggle enable/disable fallback
  const toggleFallback = (enabled: boolean) => {
    updateAISettings({ enableFallback: enabled });
    
    // If enabling fallback but no config exists, create a default one
    if (enabled && !settings.aiServices.fallback) {
      updateAISettings({
        fallback: DEFAULT_PROVIDERS.openai
      });
    }
  };

  // Test connection to a provider
  const testProviderConnection = async (configType: 'primary' | 'validation' | 'fallback') => {
    const config = settings.aiServices[configType];
    if (!config) return;
    
    setProviderTestStatus(prev => ({
      ...prev,
      [configType]: { status: 'idle', message: 'Testing connection...' }
    }));
    
    try {
      // Call the Supabase edge function to test connection
      const { data, error } = await supabase.functions.invoke('test-ai-connection', {
        body: { 
          provider: config.provider,
          config: config,
          apiKey: settings.aiServices.providerKeys?.[config.provider]
        }
      });
      
      if (error) throw error;
      
      setProviderTestStatus(prev => ({
        ...prev,
        [configType]: { 
          status: 'success', 
          message: `Successfully connected to ${config.provider} API` 
        }
      }));
    } catch (error) {
      setProviderTestStatus(prev => ({
        ...prev,
        [configType]: { 
          status: 'error', 
          message: `Connection failed: ${error instanceof Error ? error.message : String(error)}` 
        }
      }));
    }
  };

  // Render a provider configuration form
  const renderProviderForm = (
    configType: 'primary' | 'validation' | 'fallback',
    config?: ProviderConfig
  ) => {
    if (!config) {
      return (
        <div className="space-y-4 p-4 text-center">
          <p className="text-muted-foreground">No provider configured</p>
          <Button 
            onClick={() => updateProviderConfig(configType)({ provider: 'openai' })}
          >
            Configure Provider
          </Button>
        </div>
      );
    }
    
    const testStatus = providerTestStatus[configType];
    const updateConfig = updateProviderConfig(configType);
    
    return (
      <div className="space-y-6">
        {/* Provider Selection */}
        <div className="space-y-2">
          <Label htmlFor={`${configType}-provider`}>AI Provider</Label>
          <Select 
            value={config.provider} 
            onValueChange={(value: AIProvider) => {
              // When changing provider, switch to the default config for that provider
              updateProviderConfig(configType)({
                ...DEFAULT_PROVIDERS[value],
              });
            }}
          >
            <SelectTrigger id={`${configType}-provider`}>
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(DEFAULT_PROVIDERS) as AIProvider[]).map((provider) => (
                <SelectItem key={provider} value={provider}>
                  {PROVIDER_DISPLAY_NAMES[provider]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Model Selection */}
        <div className="space-y-2">
          <Label htmlFor={`${configType}-model`}>Model</Label>
          <Select 
            value={config.model} 
            onValueChange={(value) => updateConfig({ model: value })}
          >
            <SelectTrigger id={`${configType}-model`}>
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent>
              {AVAILABLE_MODELS[config.provider].map(model => (
                <SelectItem key={model} value={model}>{model}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Provider-specific settings */}
        {config.provider === 'azure' && (
          <>
            <div className="space-y-2">
              <Label htmlFor={`${configType}-resource-name`}>Resource Name</Label>
              <Input 
                id={`${configType}-resource-name`}
                value={(config as AzureConfig).resourceName || ''}
                onChange={(e) => updateConfig({ resourceName: e.target.value })}
                placeholder="my-azure-resource"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`${configType}-deployment-id`}>Deployment ID</Label>
              <Input 
                id={`${configType}-deployment-id`}
                value={(config as AzureConfig).deploymentId || ''}
                onChange={(e) => updateConfig({ deploymentId: e.target.value })}
                placeholder="gpt-4-deployment"
              />
            </div>
          </>
        )}
        
        {config.provider === 'custom' && (
          <div className="space-y-2">
            <Label htmlFor={`${configType}-endpoint`}>API Endpoint</Label>
            <Input 
              id={`${configType}-endpoint`}
              value={(config as CustomConfig).endpoint || ''}
              onChange={(e) => updateConfig({ endpoint: e.target.value })}
              placeholder="https://api.example.com/v1/chat/completions"
            />
            
            <div className="space-y-2 mt-4">
              <Label htmlFor={`${configType}-request-format`}>Request Format</Label>
              <Select 
                value={(config as CustomConfig).requestFormat || 'openai'} 
                onValueChange={(value: 'openai' | 'anthropic' | 'custom') => 
                  updateConfig({ requestFormat: value })}
              >
                <SelectTrigger id={`${configType}-request-format`}>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REQUEST_FORMATS) as RequestFormat[]).map((format) => (
                    <SelectItem key={format} value={format}>
                      {REQUEST_FORMATS[format]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {/* Advanced Settings Accordion */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="advanced">
            <AccordionTrigger>Advanced Settings</AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <Label htmlFor={`${configType}-temperature`}>
                    Temperature ({config.temperature || 0.7})
                  </Label>
                  <Input 
                    id={`${configType}-temperature`}
                    type="range"
                    min={0}
                    max={2}
                    step={0.1}
                    value={config.temperature || 0.7}
                    onChange={(e) => updateConfig({ temperature: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Precise</span>
                    <span>Creative</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`${configType}-max-tokens`}>Max Tokens</Label>
                  <Input 
                    id={`${configType}-max-tokens`}
                    type="number"
                    value={config.max_tokens || 2048}
                    onChange={(e) => updateConfig({ max_tokens: parseInt(e.target.value) })}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`${configType}-top-p`}>Top P</Label>
                  <Input 
                    id={`${configType}-top-p`}
                    type="number"
                    step={0.1}
                    min={0}
                    max={1}
                    value={config.top_p || 1}
                    onChange={(e) => updateConfig({ top_p: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                </div>

                {(config.provider === 'openai' || config.provider === 'azure') && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor={`${configType}-frequency-penalty`}>Frequency Penalty</Label>
                      <Input 
                        id={`${configType}-frequency-penalty`}
                        type="number"
                        step={0.1}
                        min={-2}
                        max={2}
                        value={config.frequency_penalty || 0}
                        onChange={(e) => updateConfig({ frequency_penalty: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor={`${configType}-presence-penalty`}>Presence Penalty</Label>
                      <Input 
                        id={`${configType}-presence-penalty`}
                        type="number"
                        step={0.1}
                        min={-2}
                        max={2}
                        value={config.presence_penalty || 0}
                        onChange={(e) => updateConfig({ presence_penalty: parseFloat(e.target.value) })}
                        className="w-full"
                      />
                    </div>
                  </>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        
        {/* Test Connection Button */}
        <div className="pt-2">
          <Button 
            variant="outline"
            onClick={() => testProviderConnection(configType)}
            className="w-full"
          >
            Test Connection
          </Button>
          
          {testStatus.status !== 'idle' && (
            <Alert variant={testStatus.status === 'success' ? 'default' : 'destructive'} className="mt-2">
              {testStatus.status === 'success' ? 
                <CheckCircle className="h-4 w-4" /> : 
                <AlertTriangle className="h-4 w-4" />
              }
              <AlertDescription>{testStatus.message}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>AI Provider Settings</CardTitle>
          <CardDescription>
            Configure and manage AI providers for your chatbot
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="primary" value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="primary">Primary</TabsTrigger>
              <TabsTrigger value="validation">Validation</TabsTrigger>
              <TabsTrigger value="fallback">Fallback</TabsTrigger>
              <TabsTrigger value="keys">API Keys</TabsTrigger>
            </TabsList>
            
            <TabsContent value="primary" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Primary AI Provider</h3>
                <p className="text-sm text-muted-foreground">
                  This provider will be used for generating all AI responses in your chatbot
                </p>
                
                {renderProviderForm('primary', settings.aiServices?.primary)}
              </div>
            </TabsContent>
            
            <TabsContent value="validation" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Validation Provider</h3>
                <p className="text-sm text-muted-foreground">
                  This provider will be used for validating user responses (can be same as primary)
                </p>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="use-primary-for-validation"
                    name="validationProvider"
                    checked={!settings.aiServices?.validation}
                    onChange={() => updateAISettings({ validation: undefined })}
                    className="h-4 w-4 text-primary"
                  />
                  <Label htmlFor="use-primary-for-validation">
                    Use primary provider for validation
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="use-separate-validation"
                    name="validationProvider"
                    checked={!!settings.aiServices?.validation}
                    onChange={() => {
                      if (!settings.aiServices?.validation) {
                        updateAISettings({ 
                          validation: { ...DEFAULT_PROVIDERS.openai } 
                        });
                      }
                    }}
                    className="h-4 w-4 text-primary"
                  />
                  <Label htmlFor="use-separate-validation">
                    Use separate provider for validation
                  </Label>
                </div>
                
                {settings.aiServices?.validation && 
                  renderProviderForm('validation', settings.aiServices.validation)}
              </div>
            </TabsContent>
            
            <TabsContent value="fallback" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Fallback Provider</h3>
                <p className="text-sm text-muted-foreground">
                  Configure a fallback provider that will be used if the primary provider fails
                </p>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="enable-fallback"
                    checked={!!settings.aiServices?.enableFallback}
                    onCheckedChange={toggleFallback}
                  />
                  <Label htmlFor="enable-fallback">
                    Enable fallback provider
                  </Label>
                </div>
                
                {settings.aiServices?.enableFallback && 
                  renderProviderForm('fallback', settings.aiServices.fallback)}
              </div>
            </TabsContent>
            
            <TabsContent value="keys" className="pt-4">
              <div className="space-y-6">
                <h3 className="text-lg font-medium">API Keys</h3>
                <p className="text-sm text-muted-foreground">
                  Manage API keys for each provider. Keys are encrypted before storage.
                </p>
                
                <Alert>
                  <Lock className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    For security, API keys are stored encrypted and never exposed in client-side code
                  </AlertDescription>
                </Alert>
                
                <div className="space-y-6 mt-4">
                  {/* OpenAI */}
                  <div className="space-y-2">
                    <Label htmlFor="openai-key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" />OpenAI API Key
                    </Label>
                    <Input
                      id="openai-key"
                      type="password"
                      placeholder="sk-..."
                      value={settings.aiServices?.providerKeys?.openai || ''}
                      onChange={(e) => updateProviderKey('openai', 'apiKey', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Get your API key from <a href="https://platform.openai.com/api-keys" className="underline" target="_blank" rel="noopener noreferrer">OpenAI dashboard</a>
                    </p>
                  </div>
                  
                  {/* Anthropic */}
                  <div className="space-y-2">
                    <Label htmlFor="anthropic-key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" />Anthropic API Key
                    </Label>
                    <Input
                      id="anthropic-key"
                      type="password"
                      placeholder="sk_ant_..."
                      value={settings.aiServices?.providerKeys?.anthropic || ''}
                      onChange={(e) => updateProviderKey('anthropic', 'apiKey', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Get your API key from <a href="https://console.anthropic.com/account/keys" className="underline" target="_blank" rel="noopener noreferrer">Anthropic console</a>
                    </p>
                  </div>
                  
                  {/* Google AI */}
                  <div className="space-y-2">
                    <Label htmlFor="google-key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" />Google AI API Key
                    </Label>
                    <Input
                      id="google-key"
                      type="password"
                      placeholder="AIza..."
                      value={settings.aiServices?.providerKeys?.google || ''}
                      onChange={(e) => updateProviderKey('google', 'apiKey', e.target.value)}
                    />
                  </div>
                  
                  {/* Azure OpenAI */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Azure OpenAI</h4>
                    <div className="space-y-2">
                      <Label htmlFor="azure-key" className="flex items-center gap-2">
                        <Key className="h-4 w-4" />API Key
                      </Label>
                      <Input
                        id="azure-key"
                        type="password"
                        placeholder="Azure API Key"
                        value={settings.aiServices?.providerKeys?.azure?.apiKey || ''}
                        onChange={(e) => updateProviderKey('azure', 'apiKey', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="azure-resource">Resource Name</Label>
                      <Input
                        id="azure-resource"
                        placeholder="my-azure-resource"
                        value={settings.aiServices?.providerKeys?.azure?.resourceName || ''}
                        onChange={(e) => updateProviderKey('azure', 'resourceName', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  {/* Mistral AI */}
                  <div className="space-y-2">
                    <Label htmlFor="mistral-key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" />Mistral AI API Key
                    </Label>
                    <Input
                      id="mistral-key"
                      type="password"
                      placeholder="..."
                      value={settings.aiServices?.providerKeys?.mistral || ''}
                      onChange={(e) => updateProviderKey('mistral', 'apiKey', e.target.value)}
                    />
                  </div>
                  
                  {/* DeepSeek AI */}
                  <div className="space-y-2">
                    <Label htmlFor="deepseek-key" className="flex items-center gap-2">
                      <Key className="h-4 w-4" />DeepSeek AI API Key
                    </Label>
                    <Input
                      id="deepseek-key"
                      type="password"
                      placeholder="dsk-..."
                      value={settings.aiServices?.providerKeys?.deepseek || ''}
                      onChange={(e) => updateProviderKey('deepseek', 'apiKey', e.target.value)}
                    />
                  </div>
                  
                  {/* Custom Provider */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Custom Provider</h4>
                    <div className="space-y-2">
                      <Label htmlFor="custom-key" className="flex items-center gap-2">
                        <Key className="h-4 w-4" />API Key
                      </Label>
                      <Input
                        id="custom-key"
                        type="password"
                        placeholder="Custom API Key"
                        value={settings.aiServices?.providerKeys?.custom?.apiKey || ''}
                        onChange={(e) => updateProviderKey('custom', 'apiKey', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-endpoint">API Endpoint</Label>
                      <Input
                        id="custom-endpoint"
                        placeholder="https://api.example.com"
                        value={settings.aiServices?.providerKeys?.custom?.endpoint || ''}
                        onChange={(e) => updateProviderKey('custom', 'endpoint', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
